import { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard, Home, FolderKanban, FileText, Building2,
  CheckSquare, File, CalendarDays, UserCog, Activity,
  LogOut, Eye, Shield, User, ListTodo, Settings
} from 'lucide-react';
import api from '@/lib/api';
import logo from '@/assets/meridian-logo-dark.png';
import NotificationCenter from '@/components/NotificationCenter';

// Nav groups (dividers between groups). Section B order.
const navGroups = [
  [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard, end: true },
    { name: 'Work Queue', path: '/work-queue', icon: ListTodo },
    { name: 'Proposals', path: '/proposals', icon: FileText },
    { name: 'Properties', path: '/properties', icon: Home },
  ],
  [
    { name: 'Projects', path: '/projects', icon: FolderKanban },
    { name: 'Clients', path: '/clients', icon: Building2 },
  ],
  [
    { name: 'Tasks', path: '/tasks', icon: CheckSquare },
    { name: 'Documents', path: '/documents', icon: File },
    { name: 'Calendar', path: '/calendar', icon: CalendarDays },
  ],
];

// Administrator-only group
const adminGroup = [
  { name: 'Employees', path: '/employees', icon: UserCog },
  { name: 'Activity Log', path: '/activity-log', icon: Activity },
];

// Administrator + Owner group (Services Library / Settings)
const adminOwnerGroup = [
  { name: 'Settings', path: '/settings/task-library', icon: Settings },
];

const navItemClass = ({ isActive }) =>
  `flex items-center gap-3 h-10 pr-4 pl-[13px] border-l-[3px] rounded-none text-sm w-full transition-colors ${
    isActive
      ? 'bg-[#F1F5F8] text-[#010A21] font-medium border-[#E5441A]'
      : 'text-[#010A21] font-normal border-transparent hover:bg-[#F1F5F8] hover:text-[#010A21]'
  }`;

export default function Layout() {
  const { user, logout, effectiveRole, viewAsRole, setViewAsRole, isRealAdmin, ALL_ROLES } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handleLogout = () => { logout(); navigate('/login'); };

  const [wqCount, setWqCount] = useState(0);
  const fetchWqCount = useCallback(() => {
    api.get('/work-queue/count').then(r => setWqCount(r.data?.attention || 0)).catch(() => {});
  }, []);
  useEffect(() => { fetchWqCount(); }, [fetchWqCount, location.pathname]);
  useEffect(() => {
    const h = () => fetchWqCount();
    window.addEventListener('workqueue-changed', h);
    return () => window.removeEventListener('workqueue-changed', h);
  }, [fetchWqCount]);

  const isAdmin = effectiveRole === 'Administrator';
  const isAdminOrOwner = effectiveRole === 'Administrator' || effectiveRole === 'Owner';

  return (
    <div className="min-h-screen bg-background">
      {/* TOP BAR - fixed, 48px, white */}
      <header
        data-testid="header"
        className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between"
        style={{ height: '64px', backgroundColor: '#132C4A', borderBottom: '1px solid #1B4A76' }}
      >
        <div data-testid="topbar-logo" style={{ paddingLeft: '20px' }}>
          <img src={logo} alt="Meridian One" style={{ height: '42px', width: 'auto', filter: 'brightness(0) invert(1)' }} />
        </div>

        <div className="flex items-center gap-1 pr-4">
          {/* Notifications */}
          <NotificationCenter />

          {/* User menu - avatar + name */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 h-8 text-[#E8EDF2] hover:bg-[#1B4A76] hover:text-white" data-testid="user-menu">
                <div className="h-6 w-6 rounded-full bg-[#1B4A76] flex items-center justify-center text-white text-xs font-semibold">
                  {user?.name?.charAt(0)}
                </div>
                <span className="text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-card border-border w-56">
              <DropdownMenuItem className="text-xs text-muted-foreground" disabled>
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuItem className="text-xs gap-2" disabled>
                <Shield className="h-3 w-3" /> {user?.role}
                {viewAsRole && <span className="ml-auto text-xs text-amber-600">Viewing: {viewAsRole}</span>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* Role switcher (Administrator only) */}
              {isRealAdmin && (
                <>
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Preview a role</div>
                  {ALL_ROLES.map(role => (
                    <DropdownMenuItem
                      key={role}
                      onClick={() => setViewAsRole(role === 'Administrator' ? null : role)}
                      className={`text-xs gap-2 ${effectiveRole === role ? 'text-primary' : ''}`}
                      data-testid={`role-option-${role.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {role === 'Administrator' ? <Shield className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      {role}
                      {effectiveRole === role && <span className="ml-auto text-primary text-xs">Active</span>}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => navigate('/profile')} className="text-xs gap-2" data-testid="nav-profile">
                <User className="h-3 w-3" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-destructive text-xs gap-2" data-testid="logout-button">
                <LogOut className="h-3 w-3" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* SIDEBAR - fixed left, 220px, navy */}
      <aside
        data-testid="sidebar"
        className="fixed left-0 z-20 flex flex-col overflow-y-auto"
        style={{
          top: '64px', width: '220px', height: 'calc(100vh - 64px)',
          backgroundColor: '#FFFFFF', borderRight: '1px solid #D4DCE8',
        }}
      >
        <nav className="flex flex-col py-2">
          {navGroups.map((group, gi) => (
            <div key={gi}>
              {gi > 0 && <div style={{ borderTop: '1px solid #E2E8F0', margin: '8px 0' }} />}
              {group.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={navItemClass}
                  style={({ isActive }) => (isActive ? { borderLeft: '3px solid #E5441A' } : undefined)}
                >
                  <item.icon style={{ height: '18px', width: '18px' }} className="shrink-0" />
                  <span style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.name}</span>
                  {item.path === '/work-queue' && wqCount > 0 && (
                    <span
                      className="ml-auto shrink-0"
                      style={{ backgroundColor: '#E5441A', color: '#ffffff', borderRadius: '10px', fontSize: '11px', fontWeight: 600, lineHeight: 1, minWidth: '18px', padding: '3px 6px', textAlign: 'center', fontFamily: "'DM Sans', sans-serif" }}
                      data-testid="nav-work-queue-badge"
                    >
                      {wqCount > 99 ? '99+' : wqCount}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}

          {/* Administrator + Owner group (Services Library) */}
          {isAdminOrOwner && (
            <div>
              <div style={{ borderTop: '1px solid #E2E8F0', margin: '8px 0' }} />
              {adminOwnerGroup.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={navItemClass}
                  style={({ isActive }) => (isActive ? { borderLeft: '3px solid #E5441A' } : undefined)}
                >
                  <item.icon style={{ height: '18px', width: '18px' }} className="shrink-0" />
                  <span style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.name}</span>
                </NavLink>
              ))}
            </div>
          )}

          {/* Administrator-only group */}
          {isAdmin && (
            <div>
              <div style={{ borderTop: '1px solid #E2E8F0', margin: '8px 0' }} />
              {adminGroup.map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={navItemClass}
                  style={({ isActive }) => (isActive ? { borderLeft: '3px solid #E5441A' } : undefined)}
                >
                  <item.icon style={{ height: '18px', width: '18px' }} className="shrink-0" />
                  <span style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.name}</span>
                </NavLink>
              ))}
            </div>
          )}
        </nav>
      </aside>

      {/* MAIN CONTENT - right of sidebar, below top bar */}
      <div style={{ marginLeft: '220px', paddingTop: '64px' }} className="min-h-screen bg-background">
        {viewAsRole && (
          <div className="bg-amber-500/10 border-b border-amber-500/30 px-6 py-2 flex items-center justify-between" data-testid="viewing-as-banner">
            <div className="flex items-center gap-2">
              <Eye className="h-3.5 w-3.5 text-amber-600" />
              <span className="text-xs text-amber-700 font-medium">
                Previewing as <span className="font-semibold">{viewAsRole}</span>: API access unchanged, UI adjusted to {"this role's view"}
              </span>
            </div>
            <Button
              variant="ghost" size="sm"
              className="text-amber-700 hover:text-amber-800 text-xs h-6 px-2"
              onClick={() => setViewAsRole(null)}
              data-testid="banner-reset-role-btn"
            >
              <Shield className="h-3 w-3 mr-1" /> Exit Preview
            </Button>
          </div>
        )}
        <main className="p-6" data-testid="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
